import type { Types } from '../types';
import DicomMetadataStore from '../services/DicomMetadataStore/DicomMetadataStore';

/**
 * TREN manual registration private-tag storage (translation-only v1).
 *
 * We store JSON strings inside private tags on *each instance* so that:
 * - tags survive export/download as real DICOM elements
 * - per-slice "slice shifts" are representable (even though the payload is the same per instance)
 *
 * Private tag layout (private group 2F47 per TREN / Craig spec):
 * - (2F47,0020) LO  PrivateCreator = "TREN_OHIF_REG" (reserves the 2000–20FF block; PS3.5 private pairing)
 * - (2F47,2000) UT  RegistrationLinkageJSON
 * - (2F47,2001) UT  RegistrationTranslationJSON
 *
 * Linkage JSON (v1) is canonicalized as:
 * {
 *   "schemaVersion": 1,
 *   "linkage": [{ "seriesInstanceUID", "seriesDescription?", "modality?" }, ...],
 *   "associateContour": "<SeriesInstanceUID of DE/SE contour source>"
 * }
 * The same object is stamped on every instance of each linked DWI-family series and on the
 * associate-contour series so all related series carry identical linkage metadata.
 */

export const TREN_PRIVATE_CREATOR = 'TREN_OHIF_REG';

export const TREN_TAG_PRIVATE_CREATOR = '2F470020';
export const TREN_TAG_LINKAGE_JSON = '2F472000';
export const TREN_TAG_TRANSLATIONS_JSON = '2F472001';

export const TREN_SCHEMA_VERSION = 1 as const;

export type TrenLinkageItem = {
  seriesInstanceUID: string;
  seriesDescription?: string;
  modality?: string;
};

export type TrenRegistrationLinkageV1 = {
  schemaVersion: typeof TREN_SCHEMA_VERSION;
  linkage: TrenLinkageItem[];
  /**
   * SeriesInstanceUID where the guide contour was drawn (DE/SE).
   * Named to match the product spec wording.
   */
  associateContour: string;
};

export type TrenRegistrationTranslationV1 = {
  schemaVersion: typeof TREN_SCHEMA_VERSION;
  /**
   * Translation in mm.
   * v1 stores *only translation* as requested.
   */
  translations_xyz_mm: [number, number, number];
};

type DicomJsonElement = {
  vr: string;
  Value?: unknown[];
};

export function trenEncodeJson(value: unknown): string {
  return JSON.stringify(value);
}

export function trenDecodeJson<T>(raw: unknown): T | undefined {
  try {
    if (!raw) {
      return;
    }

    // DICOM JSON element shape: { vr, Value: [ ... ] }
    if (typeof raw === 'object' && raw !== null && 'Value' in raw) {
      const element = raw as DicomJsonElement;
      const first = element?.Value?.[0];
      if (typeof first === 'string') {
        return JSON.parse(first) as T;
      }
      return;
    }

    if (typeof raw === 'string') {
      return JSON.parse(raw) as T;
    }
  } catch {
    // ignore malformed JSON
  }
}

export function trenMakeDicomJsonElement(vr: string, value: string): DicomJsonElement {
  return { vr, Value: [value] };
}

/**
 * Returns a metadata patch suitable for `DicomMetadataStore.updateMetadataForSeries`.
 * This patch is applied to *every instance* in the series.
 */
export function trenBuildInstanceTagPatch(params: {
  linkage?: TrenRegistrationLinkageV1;
  translations?: TrenRegistrationTranslationV1;
}): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    [TREN_TAG_PRIVATE_CREATOR]: trenMakeDicomJsonElement('LO', TREN_PRIVATE_CREATOR),
  };

  if (params.linkage) {
    patch[TREN_TAG_LINKAGE_JSON] = trenMakeDicomJsonElement('UT', trenEncodeJson(params.linkage));
  }

  if (params.translations) {
    patch[TREN_TAG_TRANSLATIONS_JSON] = trenMakeDicomJsonElement(
      'UT',
      trenEncodeJson(params.translations)
    );
  }

  return patch;
}

export function trenBuildLinkageV1(params: {
  linkage: TrenLinkageItem[];
  associateContourSeriesInstanceUID: string;
}): TrenRegistrationLinkageV1 {
  return {
    schemaVersion: TREN_SCHEMA_VERSION,
    linkage: params.linkage,
    associateContour: params.associateContourSeriesInstanceUID,
  };
}

export function trenBuildTranslationsV1(translations_xyz_mm: [number, number, number]) {
  return {
    schemaVersion: TREN_SCHEMA_VERSION,
    translations_xyz_mm,
  } satisfies TrenRegistrationTranslationV1;
}

export function trenGetLinkageFromInstance(
  instance: Record<string, unknown> | undefined
): TrenRegistrationLinkageV1 | undefined {
  if (!instance) {
    return;
  }
  return trenDecodeJson<TrenRegistrationLinkageV1>(instance[TREN_TAG_LINKAGE_JSON]);
}

export type TrenLinkageSummary = {
  studyInstanceUID: string;
  associateContour: string;
  linkage: TrenLinkageItem[];
};

/**
 * Find TREN linkage for a study. Tags are stored on DWI-family series and the associate DE/SE
 * series; exported RTSTRUCT also receives a copy for downstream use. Try multiple series UIDs
 * before scanning the study.
 */
export function trenResolveLinkageSummary(
  studyInstanceUID: string,
  seriesInstanceUIDsToTry: string[] = []
): TrenLinkageSummary | undefined {
  const tried = new Set<string>();

  const trySeries = (seriesInstanceUID: string | undefined): TrenLinkageSummary | undefined => {
    if (!seriesInstanceUID || tried.has(seriesInstanceUID)) {
      return;
    }
    tried.add(seriesInstanceUID);

    const series = DicomMetadataStore.getSeries(studyInstanceUID, seriesInstanceUID);
    const instance = series?.instances?.[0] as Record<string, unknown> | undefined;
    const linkage = trenGetLinkageFromInstance(instance);

    if (!linkage?.linkage?.length || !linkage.associateContour) {
      return;
    }

    return {
      studyInstanceUID,
      associateContour: linkage.associateContour,
      linkage: linkage.linkage,
    };
  };

  for (const uid of seriesInstanceUIDsToTry) {
    const found = trySeries(uid);
    if (found) {
      return found;
    }
  }

  const study = DicomMetadataStore.getStudy(studyInstanceUID);
  for (const series of study?.series || []) {
    const found = trySeries(series.SeriesInstanceUID);
    if (found) {
      return found;
    }
  }

  return undefined;
}

/**
 * Resolve linkage for the Segmentation panel UI so it matches Link DWI family checkboxes.
 *
 * Source of truth is the associate DE/SE series tag (same as TrenLinkageModal), not a blind
 * study-wide scan — leftover tags on an old DWI series must not show as "linked" when the
 * associate contour has no saved family.
 *
 * `seriesInstanceUIDsToTry` may include SEG/RTSTRUCT overlays (they carry a tag copy after
 * Save All) and the background anatomy series.
 */
export function trenResolveLinkageSummaryForPanel(
  studyInstanceUID: string,
  seriesInstanceUIDsToTry: string[] = []
): TrenLinkageSummary | undefined {
  const trySeries = (seriesInstanceUID: string | undefined): TrenLinkageSummary | undefined => {
    if (!seriesInstanceUID) {
      return;
    }
    const series = DicomMetadataStore.getSeries(studyInstanceUID, seriesInstanceUID);
    const instance = series?.instances?.[0] as Record<string, unknown> | undefined;
    const linkage = trenGetLinkageFromInstance(instance);
    if (!linkage?.associateContour || !linkage?.linkage?.length) {
      return;
    }
    return {
      studyInstanceUID,
      associateContour: linkage.associateContour,
      linkage: linkage.linkage,
    };
  };

  const guessRole = (seriesDescription?: string) => {
    const d = (seriesDescription || '').toUpperCase();
    if (d.includes('ADC')) return 'ADC';
    if (d.includes('BVAL')) return 'BVAL';
    if (d.includes('TRACE')) return 'TRACE';
    if (/\bDWI\b/.test(d)) return 'DWI';
    if (/\bDE\b/.test(d)) return 'DE';
    if (/\bSE\b/.test(d)) return 'SE';
    return '';
  };

  const study = DicomMetadataStore.getStudy(studyInstanceUID);
  const seriesList = (study?.series || []) as Array<{
    SeriesInstanceUID?: string;
    SeriesDescription?: string;
    Modality?: string;
  }>;

  const seriesMetaByUid = new Map(
    seriesList
      .filter(s => s.SeriesInstanceUID)
      .map(s => [s.SeriesInstanceUID as string, s] as const)
  );

  const isMr = (modality?: string) => (modality || '').toUpperCase() === 'MR';
  const deSeSeries = seriesList.filter(
    s => isMr(s.Modality) && ['DE', 'SE'].includes(guessRole(s.SeriesDescription))
  );
  const dwiFamilySeries = seriesList.filter(
    s => isMr(s.Modality) && ['ADC', 'BVAL', 'TRACE', 'DWI'].includes(guessRole(s.SeriesDescription))
  );

  // 1) Accept tags from viewport overlays (SEG/RTSTRUCT) or DE/SE only — not from DWI
  //    leftovers that would disagree with empty Link DWI checkboxes.
  for (const uid of seriesInstanceUIDsToTry) {
    const meta = seriesMetaByUid.get(uid);
    const series = DicomMetadataStore.getSeries(studyInstanceUID, uid);
    const instance = series?.instances?.[0] as
      | { Modality?: string; SeriesDescription?: string }
      | undefined;
    const mod = `${meta?.Modality || instance?.Modality || ''}`.toUpperCase();
    const role = guessRole(meta?.SeriesDescription || instance?.SeriesDescription);
    const isOverlay = mod === 'SEG' || mod === 'RTSTRUCT' || mod === 'RT';
    const isAssociateContour = role === 'DE' || role === 'SE';
    if (!isOverlay && !isAssociateContour) {
      continue;
    }
    const found = trySeries(uid);
    if (found) {
      return found;
    }
  }

  // 2) Learn associateContour from DWI-family tags (modal existingLinkage), then read the
  //    associate DE/SE series — that is what drives the checkboxes.
  let associateContourUID = seriesInstanceUIDsToTry.find(uid =>
    deSeSeries.some(s => s.SeriesInstanceUID === uid)
  );

  if (!associateContourUID) {
    for (const dwi of dwiFamilySeries) {
      const found = trySeries(dwi.SeriesInstanceUID);
      if (found?.associateContour) {
        const associateInStudy = deSeSeries.some(
          s => s.SeriesInstanceUID === found.associateContour
        );
        if (associateInStudy) {
          associateContourUID = found.associateContour;
          break;
        }
      }
    }
  }

  if (!associateContourUID) {
    associateContourUID = deSeSeries[0]?.SeriesInstanceUID;
  }

  // Only show Linked family when the associate contour series itself has a saved family.
  return trySeries(associateContourUID);
}

export function trenGetTranslationsFromInstance(
  instance: Record<string, unknown> | undefined
): TrenRegistrationTranslationV1 | undefined {
  if (!instance) {
    return;
  }
  return trenDecodeJson<TrenRegistrationTranslationV1>(instance[TREN_TAG_TRANSLATIONS_JSON]);
}

export function isTrenSchemaV1(value: any): value is { schemaVersion: 1 } {
  return value?.schemaVersion === 1;
}

/** Remove TREN registration private tags from a naturalized DICOM instance. */
export function trenStripPrivateTagsFromInstance(instance: Record<string, unknown>): void {
  delete instance[TREN_TAG_PRIVATE_CREATOR];
  delete instance[TREN_TAG_LINKAGE_JSON];
  delete instance[TREN_TAG_TRANSLATIONS_JSON];
}

/**
 * Reads linkage and translation payloads from study metadata (MR/DE source series).
 */
export function trenCollectRegistrationTagsFromStudy(
  studyInstanceUID: string,
  seriesInstanceUIDsToTry: string[] = []
): {
  linkage?: TrenRegistrationLinkageV1;
  translations?: TrenRegistrationTranslationV1;
} {
  let linkage: TrenRegistrationLinkageV1 | undefined;
  let translations: TrenRegistrationTranslationV1 | undefined;

  const summary = trenResolveLinkageSummary(studyInstanceUID, seriesInstanceUIDsToTry);
  const seriesToScan = new Set<string>(
    (seriesInstanceUIDsToTry || []).filter((uid): uid is string => typeof uid === 'string' && !!uid)
  );

  if (summary?.associateContour) {
    seriesToScan.add(summary.associateContour);
  }
  summary?.linkage?.forEach(item => {
    if (item?.seriesInstanceUID) {
      seriesToScan.add(item.seriesInstanceUID);
    }
  });

  const readTagsFromSeries = (seriesUID: string) => {
    const series = DicomMetadataStore.getSeries(studyInstanceUID, seriesUID);
    const instance = series?.instances?.[0] as Record<string, unknown> | undefined;
    if (!instance) {
      return;
    }
    if (!linkage) {
      linkage = trenGetLinkageFromInstance(instance);
    }
    if (!translations) {
      translations = trenGetTranslationsFromInstance(instance);
    }
  };

  seriesToScan.forEach(readTagsFromSeries);

  if (!linkage || !translations) {
    const study = DicomMetadataStore.getStudy(studyInstanceUID);
    for (const series of study?.series || []) {
      readTagsFromSeries(series.SeriesInstanceUID);
      if (linkage && translations) {
        break;
      }
    }
  }

  return { linkage, translations };
}

/**
 * Resolve current TREN translation (mm) from study metadata.
 * Prefers series listed in `seriesInstanceUIDsToTry` (active / family), then scans the study.
 * Returns [0,0,0] when no translation tag is present.
 */
export function trenResolveTranslationsXyzMm(
  studyInstanceUID: string,
  seriesInstanceUIDsToTry: string[] = []
): [number, number, number] {
  const { translations } = trenCollectRegistrationTagsFromStudy(
    studyInstanceUID,
    seriesInstanceUIDsToTry
  );
  const xyz = translations?.translations_xyz_mm;
  if (!Array.isArray(xyz) || xyz.length < 3) {
    return [0, 0, 0];
  }
  return [
    Number.isFinite(Number(xyz[0])) ? Number(xyz[0]) : 0,
    Number.isFinite(Number(xyz[1])) ? Number(xyz[1]) : 0,
    Number.isFinite(Number(xyz[2])) ? Number(xyz[2]) : 0,
  ];
}

/**
 * Copies current TREN linkage/translation private tags onto a naturalized instance (e.g. RTSTRUCT).
 */
export function trenApplyPrivateTagsToNaturalizedInstance(
  instance: Record<string, unknown>,
  studyInstanceUID: string,
  seriesInstanceUIDsToTry: string[] = []
): void {
  const { linkage, translations } = trenCollectRegistrationTagsFromStudy(
    studyInstanceUID,
    seriesInstanceUIDsToTry
  );

  if (!linkage && !translations) {
    return;
  }

  const patch = trenBuildInstanceTagPatch({ linkage, translations });
  Object.assign(instance, patch);
}

export type TrenRegistrationCommands = {
  setLinkage: (params: {
    studyInstanceUID: string;
    linkedSeries: TrenLinkageItem[];
    associateContourSeriesInstanceUID: string;
  }) => void;
  setTranslations: (params: {
    studyInstanceUID: string;
    linkedSeriesInstanceUIDs: string[];
    translations_xyz_mm: [number, number, number];
  }) => void;
};
